"use client"

import { useRouter } from "next/navigation"
import { useState } from "react"

import { markAllNotificationsAction } from "@/app/(bare)/notifications/actions"
import type { Notification } from "@/features/v2/notifications/types"

import { Card } from "./components/Card"
import { Empty } from "./components/Empty"
import { Filters } from "./components/Filters"
import { MarkAll } from "./components/MarkAll"
import { matchesNotificationFilter, type NotificationFilterId } from "./filter"
import {
  isNotificationFromToday,
  notificationAge,
  type NotificationAge
} from "./format-age"
import { screenTexts } from "./localization/screen"
import { parseNotificationBody } from "./parse-body"
import { notificationTypeLabelKey } from "./type-label"

function ageText(age: NotificationAge): string {
  if (age.unit === "now") return screenTexts.age.now
  if (age.unit === "minute") return screenTexts.age.minutes(age.count)
  if (age.unit === "hour") return screenTexts.age.hours(age.count)
  return screenTexts.age.days(age.count)
}

type NotificationsScreenProps = {
  notifications: Notification[]
}

export function NotificationsScreen({
  notifications
}: NotificationsScreenProps) {
  const router = useRouter()
  const [filter, setFilter] = useState<NotificationFilterId>("all")
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [readIds, setReadIds] = useState<readonly string[]>([])
  const [markAllFailed, setMarkAllFailed] = useState(false)
  const now = new Date()

  const active = notifications.filter((item) => !item.deleted)
  const counts: Record<NotificationFilterId, number> = {
    all: active.length,
    promotions: active.filter((item) =>
      matchesNotificationFilter(item.type, "promotions")
    ).length,
    payments: active.filter((item) =>
      matchesNotificationFilter(item.type, "payments")
    ).length
  }
  const visible = active.filter((item) =>
    matchesNotificationFilter(item.type, filter)
  )
  const today = visible.filter((item) =>
    isNotificationFromToday(new Date(item.created_at), now)
  )
  const earlier = visible.filter(
    (item) => !isNotificationFromToday(new Date(item.created_at), now)
  )

  const visibleExpandedId = visible.some(
    (item) => String(item.id) === expandedId
  )
    ? expandedId
    : null

  function selectFilter(next: NotificationFilterId) {
    setFilter(next)
    setExpandedId(null)
  }

  function toggle(id: string) {
    setExpandedId((current) => (current === id ? null : id))
  }

  async function markAll() {
    try {
      const result = await markAllNotificationsAction()
      if (!result.ok) {
        setMarkAllFailed(true)
        return
      }

      const unreadIds = notifications
        .filter((item) => !item.deleted && !item.read)
        .map((item) => String(item.id))

      setReadIds((current) => [...current, ...unreadIds])
      setMarkAllFailed(false)
      router.refresh()
    } catch {
      setMarkAllFailed(true)
    }
  }

  return (
    <div
      className={`flex w-full flex-col gap-3 ${visible.length === 0 ? "" : "pb-8"}`}
    >
      <Filters selected={filter} counts={counts} onSelect={selectFilter} />
      {visible.length === 0 ? null : (
        <MarkAll failed={markAllFailed} onMarkAll={markAll} />
      )}
      {visible.length === 0 ? (
        <Empty />
      ) : (
        <>
          <AgeGroup
            label={screenTexts.groups.today}
            items={today}
            now={now}
            readIds={readIds}
            expandedId={visibleExpandedId}
            onToggle={toggle}
          />
          <AgeGroup
            label={screenTexts.groups.earlier}
            items={earlier}
            now={now}
            readIds={readIds}
            expandedId={visibleExpandedId}
            onToggle={toggle}
          />
        </>
      )}
    </div>
  )
}

type AgeGroupProps = {
  label: string
  items: Notification[]
  now: Date
  readIds: readonly string[]
  expandedId: string | null
  onToggle: (id: string) => void
}

function AgeGroup({
  label,
  items,
  now,
  readIds,
  expandedId,
  onToggle
}: AgeGroupProps) {
  if (items.length === 0) return null

  return (
    <>
      <h2 className="text-sm text-white/75">{label}</h2>
      <div className="flex flex-col gap-3" role="list">
        {items.map((item) => {
          const id = String(item.id)
          const body = parseNotificationBody(item.body)
          const unread = !item.read && !readIds.includes(id)

          return (
            <Card
              key={id}
              title={body.title}
              description={body.description}
              label={screenTexts.labels[notificationTypeLabelKey(item.type)]}
              time={ageText(notificationAge(new Date(item.created_at), now))}
              unread={unread}
              expanded={id === expandedId}
              actionUrl={body.actionUrl}
              actionLabel={body.actionLabel}
              onToggle={() => onToggle(id)}
            />
          )
        })}
      </div>
    </>
  )
}
